import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Search, Calendar, MapPin, Loader2, ClipboardList,
  Smartphone, Monitor, X, ExternalLink, AlertTriangle, ChevronDown,
} from 'lucide-react';
import {
  adminApi,
  type AttendanceHistoryRecord,
  type AttendanceKindergartenItem,
} from '../../services/api';
import { adminColors as colors } from '../../styles/colors';
import { useOrgFilter } from '../../hooks/useOrgFilter';

// Format yyyyMMdd to dd/MM/yyyy
const formatDate = (dateStr: string): string => {
  if (!dateStr || dateStr.length !== 8) return dateStr;
  const y = dateStr.substring(0, 4);
  const m = dateStr.substring(4, 6);
  const d = dateStr.substring(6, 8);
  return d + '/' + m + '/' + y;
};

// Format HTML date (yyyy-MM-dd) to dd/MM/yyyy for display
const formatHtmlDate = (htmlDate: string): string => {
  if (!htmlDate) return '';
  const parts = htmlDate.split('-');
  if (parts.length !== 3) return htmlDate;
  return parts[2] + '/' + parts[1] + '/' + parts[0];
};

const extractTime = (isoStr: string): string => {
  const match = String(isoStr).match(/T(\d{2}:\d{2})/);
  return match ? match[1] : '';
};

const sourceHebrew: Record<string, string> = {
  'Tablet': 'טאבלט',
  'ParentApp': 'אפליקציית הורים',
  'Staff': 'צוות',
  'BleTag': 'תג',
};

const sourceLabel = (source: string): string => sourceHebrew[source] || source || '--';

// Get today as yyyy-MM-dd for max date attribute
const getTodayStr = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Check if date range exceeds 1 month
const isOverOneMonth = (from: string, to: string): boolean => {
  if (!from || !to) return false;
  const d1 = new Date(from);
  const d2 = new Date(to);
  const diffMs = d2.getTime() - d1.getTime();
  return diffMs > 31 * 24 * 60 * 60 * 1000;
};

const STR = {
  title: 'היסטוריית נוכחות',
  subtitle: 'צפייה בהיסטוריית הגעות ילדים לגן לפי תאריכים',
  gan: 'גן *',
  child: 'ילד',
  fromDate: 'מתאריך *',
  toDate: 'עד תאריך *',
  date: 'תאריך *',
  search: 'חיפוש',
  clear: 'נקה',
  loading: 'טוען',
  chooseGan: 'בחר גן',
  chooseChild: 'בחר ילד',
  allChildren: 'כל הילדים',
  loadingDots: 'טוען...',
  filterByChild: 'סנן לפי שם ילד...',
  records: 'רשומות',
  noRecords: 'לא נמצאו רשומות נוכחות',
  loadingData: 'טוען נתונים...',
  emptyState: 'בחר גן ותאריך להצגת היסטוריית נוכחות',
  colDate: 'תאריך',
  colChild: 'שם ילד',
  colExpected: 'שעה צפויה',
  colArrived: 'שעת הגעה',
  colSource: 'מקור',
  colMarkedBy: 'סומן ע"י',
  colLocation: 'מיקום',
  // חיפוש ילד ספציפי מאפשר טווח תאריכים. כל הילדים - תאריך ספציפי בלבד
  modeChildHint: 'בחירת ילד מאפשרת חיפוש בטווח תאריכים',
  modeAllHint: 'כל הילדים - תאריך ספציפי בלבד',
  rangeWarningTitle: 'אזהרה',
  rangeWarningMsg: 'טווח תאריכים גדול מחודש. החיפוש עלול להיות איטי.',
  rangeWarningContinue: 'המשך',
  rangeWarningCancel: 'ביטול',
};

interface ChildOption {
  id: number;
  name: string;
}

export default function AdminAttendanceHistory() {
  const { orgFilter } = useOrgFilter();
  const todayStr = useMemo(() => getTodayStr(), []);

  const [kindergartens, setKindergartens] = useState<AttendanceKindergartenItem[]>([]);
  const [selectedKgId, setSelectedKgId] = useState<number | null>(null);
  const [selectedKgPublicId, setSelectedKgPublicId] = useState<string>('');
  const [children, setChildren] = useState<ChildOption[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [childSearch, setChildSearch] = useState('');
  const [history, setHistory] = useState<AttendanceHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingKgs, setLoadingKgs] = useState(true);
  const [kgLoadError, setKgLoadError] = useState(false);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showRangeWarning, setShowRangeWarning] = useState(false);
  const [childDropdownOpen, setChildDropdownOpen] = useState(false);
  const [childDropdownSearch, setChildDropdownSearch] = useState('');
  const childDropdownRef = useRef<HTMLDivElement>(null);
  const childSearchInputRef = useRef<HTMLInputElement>(null);

  // Close child dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (childDropdownRef.current && !childDropdownRef.current.contains(e.target as Node)) {
        setChildDropdownOpen(false);
        setChildDropdownSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (childDropdownOpen && childSearchInputRef.current) {
      childSearchInputRef.current.focus();
    }
  }, [childDropdownOpen]);

  // Filtered children for dropdown
  const filteredChildren = useMemo(() => {
    if (!childDropdownSearch.trim()) return children;
    const q = childDropdownSearch.trim().toLowerCase();
    return children.filter(c => c.name.toLowerCase().includes(q));
  }, [children, childDropdownSearch]);

  // Selected child display name
  const selectedChildName = selectedChildId !== null ? children[selectedChildId]?.name : null;

  // Is child selected? This determines single-date vs date-range mode
  const isChildMode = selectedChildId !== null;

  // Load kindergartens for dropdown
  const [kgRefreshKey, setKgRefreshKey] = useState(0);
  useEffect(() => {
    const loadKgs = async () => {
      setLoadingKgs(true);
      setKgLoadError(false);
      try {
        const result = await adminApi.getAttendanceKindergartens(orgFilter);
        if (result.success && result.data) {
          setKindergartens(result.data);
        } else {
          setKgLoadError(true);
        }
      } catch (err) {
        console.error('Failed to load kindergartens:', err);
        setKgLoadError(true);
      } finally {
        setLoadingKgs(false);
      }
    };
    loadKgs();
  }, [orgFilter, kgRefreshKey]);

  // Load children when kindergarten changes
  useEffect(() => {
    if (!selectedKgPublicId) {
      setChildren([]);
      setSelectedChildId(null);
      return;
    }
    const loadChildren = async () => {
      setLoadingChildren(true);
      try {
        const result = await adminApi.getChildren(orgFilter);
        if (result.success && result.data) {
          const kgChildren = result.data
            .filter(c => c.kindergartenPublicId === selectedKgPublicId)
            .map(c => ({ id: 0, name: `${c.firstName} ${c.lastName}`, publicId: c.publicId }));
          // We need child IDs for the API, but adminApi.getChildren returns publicIds
          // We'll search by name after fetching all results
          setChildren(kgChildren.map((c, i) => ({ id: i, name: c.name })));
        }
      } catch (err) {
        console.error('Failed to load children:', err);
      } finally {
        setLoadingChildren(false);
      }
    };
    loadChildren();
  }, [selectedKgPublicId, orgFilter]);

  const toApiDate = (htmlDate: string): string => htmlDate.replace(/-/g, '');

  const handleSearch = async (force?: boolean) => {
    if (!selectedKgId || !dateFrom) return;

    // For "all children" mode, dateTo must equal dateFrom (single date)
    const effectiveDateTo = isChildMode ? (dateTo || dateFrom) : dateFrom;

    // Warn if date range > 1 month (only in child mode)
    if (isChildMode && !force && isOverOneMonth(dateFrom, effectiveDateTo)) {
      setShowRangeWarning(true);
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const result = await adminApi.getAttendanceHistory({
        kindergartenId: selectedKgId,
        dateFrom: toApiDate(dateFrom),
        dateTo: toApiDate(effectiveDateTo),
      });
      if (result.success && result.data) {
        let data = result.data;
        // If child mode, filter by child name client-side
        if (isChildMode && selectedChildId !== null) {
          const childName = children[selectedChildId]?.name;
          if (childName) {
            data = data.filter(r => r.childName === childName);
          }
        }
        setHistory(data);
      } else {
        setHistory([]);
      }
    } catch (err) {
      console.error('Failed to load attendance history:', err);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedKgId(null);
    setSelectedKgPublicId('');
    setSelectedChildId(null);
    setDateFrom('');
    setDateTo('');
    setChildSearch('');
    setHistory([]);
    setSearched(false);
  };

  const handleKgChange = (value: string) => {
    const kgId = value ? Number(value) : null;
    setSelectedKgId(kgId);
    const kg = kindergartens.find(k => k.id === kgId);
    setSelectedKgPublicId(kg?.publicId || '');
    setSelectedChildId(null);
    setHistory([]);
    setSearched(false);
  };

  const handleChildChange = (childIdx: number | null) => {
    setSelectedChildId(childIdx);
    setChildDropdownOpen(false);
    setChildDropdownSearch('');
    // Reset dateTo when switching between child/all mode
    if (childIdx === null) {
      setDateTo('');
    }
  };

  // Determine if search is valid
  const canSearch = selectedKgId !== null && dateFrom !== '' && (!isChildMode || true);

  // Client-side filter: child name text
  const filteredHistory = useMemo(() => {
    if (!childSearch.trim()) return history;
    const q = childSearch.trim().toLowerCase();
    return history.filter(h => h.childName.toLowerCase().includes(q));
  }, [history, childSearch]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
          {STR.title}
        </h1>
        <p style={{ fontSize: '14px', color: colors.textLight }}>
          {STR.subtitle}
        </p>
      </div>

      {/* Filters */}
      <div style={{
        padding: '16px',
        borderRadius: '14px',
        backgroundColor: colors.primaryLight,
        border: `1px solid ${colors.primary}30`,
        direction: 'rtl',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          {/* Kindergarten */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: colors.text, fontWeight: '600' }}>{STR.gan}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <select
                value={selectedKgId ?? ''}
                onChange={(e) => handleKgChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${kgLoadError ? colors.error : colors.border}`,
                  fontSize: '13px',
                  color: colors.text,
                  minWidth: '180px',
                  backgroundColor: '#fff',
                }}
              >
                <option value="">{loadingKgs ? STR.loadingDots : kgLoadError ? String.fromCharCode(0x05E9, 0x05D2, 0x05D9, 0x05D0, 0x05D4) : STR.chooseGan}</option>
                {kindergartens.map(kg => (
                  <option key={kg.id} value={kg.id}>{kg.name}</option>
                ))}
              </select>
              {kgLoadError && (
                <button
                  type="button"
                  onClick={() => setKgRefreshKey(k => k + 1)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.error}`,
                    backgroundColor: '#fff',
                    color: colors.error,
                    fontSize: '12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {String.fromCharCode(0x05E0, 0x05E1, 0x05D4, 0x0020, 0x05E9, 0x05D5, 0x05D1)}
                </button>
              )}
            </div>
          </div>

          {/* Child (optional) — searchable dropdown */}
          {selectedKgId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' }} ref={childDropdownRef}>
              <label style={{ fontSize: '12px', color: colors.text, fontWeight: '600' }}>{STR.child}</label>
              <button
                type="button"
                onClick={() => { if (!loadingChildren) setChildDropdownOpen(!childDropdownOpen); }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${childDropdownOpen ? colors.primary : colors.border}`,
                  fontSize: '13px',
                  color: selectedChildName ? colors.text : colors.textLight,
                  minWidth: '200px',
                  backgroundColor: '#fff',
                  cursor: loadingChildren ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  textAlign: 'right',
                  transition: 'border-color 0.2s',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {loadingChildren ? STR.loadingDots : selectedChildName || STR.allChildren}
                </span>
                <ChevronDown size={14} style={{ flexShrink: 0, transform: childDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {childDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  width: '260px',
                  maxHeight: '280px',
                  backgroundColor: '#fff',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 50,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  {/* Search input */}
                  <div style={{ padding: '8px', borderBottom: `1px solid ${colors.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: colors.background }}>
                      <Search size={13} color={colors.textLight} />
                      <input
                        ref={childSearchInputRef}
                        type="text"
                        value={childDropdownSearch}
                        onChange={(e) => setChildDropdownSearch(e.target.value)}
                        placeholder={STR.filterByChild}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          fontSize: '13px',
                          direction: 'rtl',
                          backgroundColor: 'transparent',
                          color: colors.text,
                        }}
                      />
                      {childDropdownSearch && (
                        <button onClick={() => setChildDropdownSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '1px', display: 'flex' }}>
                          <X size={12} color={colors.textLight} />
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Options list */}
                  <div style={{ overflowY: 'auto', maxHeight: '220px' }}>
                    {/* "All children" option */}
                    <button
                      type="button"
                      onClick={() => handleChildChange(null)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        backgroundColor: selectedChildId === null ? colors.primaryLight : 'transparent',
                        color: selectedChildId === null ? colors.primary : colors.text,
                        fontSize: '13px',
                        fontWeight: selectedChildId === null ? '600' : '400',
                        cursor: 'pointer',
                        textAlign: 'right',
                        direction: 'rtl',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => { if (selectedChildId !== null) (e.target as HTMLElement).style.backgroundColor = colors.background; }}
                      onMouseLeave={(e) => { if (selectedChildId !== null) (e.target as HTMLElement).style.backgroundColor = 'transparent'; }}
                    >
                      {STR.allChildren}
                    </button>
                    {filteredChildren.length === 0 ? (
                      <div style={{ padding: '16px 12px', textAlign: 'center', fontSize: '12px', color: colors.textLight }}>
                        {STR.noRecords}
                      </div>
                    ) : (
                      filteredChildren.map((child) => {
                        const originalIdx = children.indexOf(child);
                        const isSelected = selectedChildId === originalIdx;
                        return (
                          <button
                            key={originalIdx}
                            type="button"
                            onClick={() => handleChildChange(originalIdx)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              backgroundColor: isSelected ? colors.primaryLight : 'transparent',
                              color: isSelected ? colors.primary : colors.text,
                              fontSize: '13px',
                              fontWeight: isSelected ? '600' : '400',
                              cursor: 'pointer',
                              textAlign: 'right',
                              direction: 'rtl',
                              transition: 'background-color 0.15s',
                            }}
                            onMouseEnter={(e) => { if (!isSelected) (e.target as HTMLElement).style.backgroundColor = colors.background; }}
                            onMouseLeave={(e) => { if (!isSelected) (e.target as HTMLElement).style.backgroundColor = 'transparent'; }}
                          >
                            {child.name}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Date From */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: colors.text, fontWeight: '600' }}>
              {isChildMode ? STR.fromDate : STR.date}
            </label>
            <div style={{ position: 'relative', minWidth: '150px' }}>
              <input
                type="date"
                value={dateFrom}
                max={todayStr}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  if (!isChildMode) {
                    setDateTo(e.target.value);
                  }
                }}
                className="date-input-overlay"
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  fontSize: '13px',
                  color: 'transparent',
                  backgroundColor: '#fff',
                  width: '100%',
                  cursor: 'pointer',
                  direction: 'ltr',
                }}
              />
              <div style={{ position: 'absolute', top: '50%', right: '12px', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '13px', color: dateFrom ? colors.text : colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={14} color={colors.textLight} />
                {dateFrom ? formatHtmlDate(dateFrom) : 'dd/mm/yyyy'}
              </div>
            </div>
          </div>

          {/* Date To (only in child mode) */}
          {isChildMode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: colors.text, fontWeight: '600' }}>{STR.toDate}</label>
              <div style={{ position: 'relative', minWidth: '150px' }}>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  max={todayStr}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="date-input-overlay"
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    fontSize: '13px',
                    color: 'transparent',
                    backgroundColor: '#fff',
                    width: '100%',
                    cursor: 'pointer',
                    direction: 'ltr',
                  }}
                />
                <div style={{ position: 'absolute', top: '50%', right: '12px', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '13px', color: dateTo ? colors.text : colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Calendar size={14} color={colors.textLight} />
                  {dateTo ? formatHtmlDate(dateTo) : 'dd/mm/yyyy'}
                </div>
              </div>
            </div>
          )}

          {/* Search button */}
          <button
            onClick={() => handleSearch()}
            disabled={!canSearch || loading}
            style={{
              padding: '8px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: !canSearch ? colors.border : colors.primary,
              color: '#fff',
              fontSize: '13px',
              fontWeight: '600',
              cursor: !canSearch ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={14} />}
            {STR.search}
          </button>
          {searched && (
            <button
              onClick={handleClear}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: '#fff',
                color: colors.textLight,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {STR.clear}
            </button>
          )}
        </div>

        {/* Mode hint */}
        <div style={{ marginTop: '8px', fontSize: '11px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ opacity: 0.6 }}>*</span>
          {isChildMode ? STR.modeChildHint : STR.modeAllHint}
        </div>
      </div>

      {/* Child name text filter (after search, for "all children" mode) */}
      {searched && history.length > 0 && !isChildMode && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          borderRadius: '10px',
          backgroundColor: '#fff',
          border: `1px solid ${colors.border}`,
        }}>
          <Search size={16} color={colors.textLight} />
          <input
            type="text"
            value={childSearch}
            onChange={(e) => setChildSearch(e.target.value)}
            placeholder={STR.filterByChild}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              direction: 'rtl',
              backgroundColor: 'transparent',
              color: colors.text,
            }}
          />
          {childSearch && (
            <button
              onClick={() => setChildSearch('')}
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px' }}
            >
              <X size={14} color={colors.textLight} />
            </button>
          )}
          <span style={{ fontSize: '12px', color: colors.textLight }}>
            {filteredHistory.length} {STR.records}
          </span>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <Loader2 size={32} color={colors.primary} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
          <p style={{ fontSize: '14px', color: colors.textLight }}>{STR.loadingData}</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : searched && filteredHistory.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: '#fff',
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
        }}>
          <Calendar size={48} color={colors.textLight} style={{ marginBottom: '12px' }} />
          <p style={{ fontSize: '16px', color: colors.textLight }}>
            {STR.noRecords}
          </p>
        </div>
      ) : searched ? (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '14px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}>
          {/* Record count */}
          <div style={{ padding: '10px 16px', fontSize: '13px', color: colors.textLight, borderBottom: `1px solid ${colors.border}` }}>
            {filteredHistory.length} {STR.records}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: colors.background }}>
                  <th style={thStyle}>{STR.colDate}</th>
                  <th style={thStyle}>{STR.colChild}</th>
                  <th style={thStyle}>{STR.colExpected}</th>
                  <th style={thStyle}>{STR.colArrived}</th>
                  <th style={thStyle}>{STR.colSource}</th>
                  <th style={thStyle}>{STR.colMarkedBy}</th>
                  <th style={thStyle}>{STR.colLocation}</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((record, index) => (
                  <motion.tr
                    key={`${record.childId}-${record.date}-${index}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(index * 0.02, 0.5) }}
                    style={{
                      borderBottom: `1px solid ${colors.border}`,
                      backgroundColor: index % 2 === 0 ? '#fff' : colors.background,
                    }}
                  >
                    <td style={tdStyle}>{formatDate(record.date)}</td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: '500', color: colors.text }}>{record.childName || '--'}</span>
                    </td>
                    <td style={tdStyle}>{record.expectedArrivalTime || '--'}</td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: '600', color: colors.primary }}>
                        {extractTime(record.arrivedAt) || '--'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: record.source === 'Tablet' ? colors.secondaryLight : record.source === 'ParentApp' ? colors.primaryLight : colors.background,
                        color: record.source === 'Tablet' ? colors.secondary : record.source === 'ParentApp' ? colors.primary : colors.textLight,
                      }}>
                        {record.source === 'Tablet' ? <Monitor size={12} /> : <Smartphone size={12} />}
                        {sourceLabel(record.source)}
                      </span>
                    </td>
                    <td style={tdStyle}>{record.markedByName || '--'}</td>
                    <td style={tdStyle}>
                      {record.latitude && record.longitude ? (
                        <a
                          href={`https://maps.google.com/?q=${record.latitude},${record.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            color: colors.primary,
                            textDecoration: 'none',
                            fontSize: '12px',
                          }}
                        >
                          <MapPin size={13} />
                          <ExternalLink size={11} />
                        </a>
                      ) : (
                        <span style={{ color: colors.textLight }}>{'--'}</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: '#fff',
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
        }}>
          <ClipboardList size={48} color={colors.textLight} style={{ marginBottom: '12px' }} />
          <p style={{ fontSize: '16px', color: colors.textLight }}>
            {STR.emptyState}
          </p>
        </div>
      )}

      {/* Range Warning Modal */}
      {showRangeWarning && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.5)',
        }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              direction: 'rtl',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: colors.warningLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <AlertTriangle size={20} color={colors.warning} />
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: colors.text }}>
                {STR.rangeWarningTitle}
              </h3>
            </div>
            <p style={{ fontSize: '14px', color: colors.textLight, marginBottom: '20px', lineHeight: '1.6' }}>
              {STR.rangeWarningMsg}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-start' }}>
              <button
                onClick={() => { setShowRangeWarning(false); handleSearch(true); }}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: colors.warning,
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                {STR.rangeWarningContinue}
              </button>
              <button
                onClick={() => setShowRangeWarning(false)}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: '#fff',
                  color: colors.textLight,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                {STR.rangeWarningCancel}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'right',
  fontWeight: '600',
  fontSize: '12px',
  color: '#6b7280',
  borderBottom: '2px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
};
